// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.22.0 (OD-A7) — an application-wide keyboard shortcut.
 *
 * Measured at kickoff: nothing in the frontend bound a shortcut hook. The
 * existing window/document `keydown` listeners are local (focus traps, the
 * mobile sidebar, date pickers, the tour runner) or the UI Assist overlay's
 * press-and-hold activation key, and none binds Ctrl/⌘+Shift+F. So this is
 * the hook, registered in REUSE.md.
 *
 * The primary modifier is Ctrl on Windows/Linux and ⌘ on macOS; both are
 * accepted everywhere, so an operator on a Mac keyboard attached to a Windows
 * machine is not locked out. The shortcut is ignored:
 *   - while focus is in a text input, textarea, select or contenteditable —
 *     typing is never hijacked;
 *   - while any dialog is open, modal or not — a shortcut must not open a
 *     second modal on top of one that is trapping focus, nor on top of a
 *     non-modal dialog such as the notification drawer, which the modal's
 *     focus trap would then strand behind its backdrop (gate E12.5 pass 1);
 *   - on key repeat, and when another handler already consumed the event.
 */

import { useEffect, useRef } from 'react';

export interface GlobalShortcut {
  /** `KeyboardEvent.code` of the letter, e.g. `KeyF`. */
  code: string;
  shift?: boolean;
  alt?: boolean;
}

const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
  'date',
  'datetime-local',
  'month',
  'week',
  'time',
]);

/** True when a keystroke at `target` is typing, not a command. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.closest('[contenteditable]:not([contenteditable="false"])')) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type || 'text';
    return TEXT_INPUT_TYPES.has(type);
  }
  return false;
}

/**
 * True when a dialog is open anywhere in the document — `aria-modal` or not.
 * A dialog that is in the DOM but hidden (`hidden`, or inside a hidden
 * ancestor) does not count.
 */
export function isModalOpen(doc: Document = document): boolean {
  const dialogs = doc.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]');
  return Array.from(dialogs).some((el) => !el.closest('[hidden]'));
}

export function matchesShortcut(event: KeyboardEvent, shortcut: GlobalShortcut): boolean {
  if (!(event.ctrlKey || event.metaKey)) return false;
  if (Boolean(shortcut.shift) !== event.shiftKey) return false;
  if (Boolean(shortcut.alt) !== event.altKey) return false;
  if (event.code === shortcut.code) return true;
  // Some layouts report no `code`; fall back to the letter.
  const letter = shortcut.code.startsWith('Key') ? shortcut.code.slice(3).toLowerCase() : '';
  return letter !== '' && event.key.toLowerCase() === letter;
}

export function useGlobalShortcut(
  shortcut: GlobalShortcut,
  onTrigger: () => void,
  enabled = true,
): void {
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;
  const { code, shift, alt } = shortcut;

  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      if (!matchesShortcut(event, { code, shift, alt })) return;
      if (isTypingTarget(event.target)) return;
      if (isModalOpen()) return;
      event.preventDefault();
      onTriggerRef.current();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, code, shift, alt]);
}
